import React, { useRef } from "react";
import { Carousel } from "../../atoms";
import get from "lodash/get";
import { getStoryElementByType } from "../../utils";
import YouTube from "react-youtube";
import getYouTubeID from "get-youtube-id";
import "./VideoInStory.m.css";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";

const VideoInStory = ({ moreVideos, story, config }) => {
  const pageType = "story-page";
  const playersRef = useRef({});
  const startedVideosRef = useRef(new Set());
  const userHasInteractedRef = useRef(false); // ⭐ real interaction tracker

  const pauseOtherVideos = (currentVideoId) => {
    Object.entries(playersRef.current).forEach(([id, player]) => {
      if (id !== currentVideoId && player?.pauseVideo) {
        player.pauseVideo();
      }
    });
  };

  const onPlayerReady = (event, shouldAutoPlay, videoId) => {
    playersRef.current[videoId] = event.target;

    if (shouldAutoPlay) {
      event.target.mute(); // required for autoplay
      event.target.playVideo();
    }
  };

  const pushVideoEvent = ({ action, title, videoId, videoUrl, index }) => {
    if (!window?.dataLayer) return;

    const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);
    window.dataLayer.push({
      event: "video_view",
      event_action: action,
      link_text: title,
      link_url: videoUrl,
      category,
      sub_category: subCategory,
      content_id: contentId,
      template_name: getTemplateName(story?.["story-template"] || pageType),
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
  };

  return (
    <div
      styleName="video-in-story"
      onMouseDown={() => (userHasInteractedRef.current = true)}
      onTouchStart={() => (userHasInteractedRef.current = true)}
    >
      <div styleName="video-in-story-header">
        <a href={`/${moreVideos?.slug}`}>
          <h2>Top Videos</h2>
        </a>
        <a href={`/${moreVideos?.slug}`} styleName="watch-more-button">
          Watch More
        </a>
      </div>

      <div styleName="video-in-story-body">
        <Carousel itemWidth={290} gap={12} showButtons={true} maxItemWidth={290} itemMobileWidth={300}>
          {moreVideos?.items?.map((video, index) => {
            let eventEmbed = "";

            video?.story?.cards?.forEach((card) => {
              const embedUrl = get(getStoryElementByType(card, "youtube-video"), "embed-url", "");
              if (embedUrl) eventEmbed = embedUrl;
            });

            if (!eventEmbed) return null;

            const ytId = getYouTubeID(eventEmbed);
            const isFirstVideo = index === 0;

            return (
              <div key={index} styleName="video-in-story-item">
                <YouTube
                  videoId={ytId}
                  opts={{
                    width: "100%",
                    height: "100%",
                    playerVars: {
                      autoplay: isFirstVideo ? 1 : 0,
                      mute: 1, // autoplay always muted
                      controls: 1,
                      rel: 0,
                      modestbranding: 1,
                      playsinline: 1,
                    },
                  }}
                  onReady={(e) => onPlayerReady(e, isFirstVideo, ytId)}
                  onStateChange={(e) => {
                    const state = e.data;
                    const title = video?.story?.headline;
                    const videoUrl = `/${video?.story?.slug}`;
                    const player = playersRef.current[ytId];

                    if (state === 1) {
                      pauseOtherVideos(ytId);

                      // ⭐ After ANY real user click → always unmute
                      if (userHasInteractedRef.current) {
                        player?.unMute();
                      }

                      if (!startedVideosRef.current.has(ytId)) {
                        startedVideosRef.current.add(ytId);

                        pushVideoEvent({
                          action: "start",
                          title,
                          videoId: ytId,
                          videoUrl,
                          index,
                        });
                      }

                      pushVideoEvent({
                        action: "play",
                        title,
                        videoId: ytId,
                        videoUrl,
                        index,
                      });
                    }

                    if (state === 2) {
                      pushVideoEvent({
                        action: "pause",
                        title,
                        videoId: ytId,
                        videoUrl,
                        index,
                      });
                    }

                    if (state === 0) {
                      pushVideoEvent({
                        action: "end",
                        title,
                        videoId: ytId,
                        videoUrl,
                        index,
                      });
                    }
                  }}
                />

                <h3>
                  <a href={`/${video?.story?.slug}`}>{video?.story?.headline}</a>
                </h3>
              </div>
            );
          })}
        </Carousel>
      </div>
    </div>
  );
};

export default VideoInStory;
